import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { TranslocoPipe } from '@jsverse/transloco';

import { IconComponent } from '@shared/components';
import { EllipsisTooltipDirective } from '@shared/directives/tooltip';

import { isRouteActive, stripTenantSegment } from '../models/navigation.model';

import type { NavigationRootItem } from '../models/navigation.model';

/**
 * Dev-only navigation item.
 *
 * The sibling of {@link NavItemComponent} for reserved top-level routes that are
 * NOT tenant-scoped (e.g. `/components`). It is intentionally separate because
 * `NavItemComponent` binds `[appTenantLink]`, which unconditionally prefixes the
 * current tenant — that would produce a broken `/{tenant}/components` link. This
 * component binds a plain absolute `[routerLink]` instead. Active-state and
 * collapsed/expanded rendering mirror {@link NavItemComponent}.
 */
@Component({
  selector: 'app-nav-dev-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, IconComponent, TranslocoPipe, EllipsisTooltipDirective],
  host: {
    class: 'block',
  },
  template: `
    @if (collapsed()) {
      <!-- Collapsed mode: icon-only button with tooltip -->
      <a
        #tooltipTrigger
        [routerLink]="item().route"
        class="nav-section-trigger nav-tooltip justify-center"
        [attr.data-active]="isActive()"
        [attr.aria-current]="isActive() ? 'page' : null"
        [attr.aria-label]="item().label | transloco"
        [attr.data-tooltip]="item().label | transloco"
        (mouseenter)="showTooltip(tooltipTrigger)"
        (mouseleave)="hideTooltip(tooltipTrigger)"
      >
        <app-icon [name]="item().icon" />
      </a>
    } @else {
      <!-- Expanded mode: full item -->
      <a
        [routerLink]="item().route"
        class="nav-item"
        [attr.data-active]="isActive()"
        [attr.aria-current]="isActive() ? 'page' : null"
      >
        <app-icon [name]="item().icon" />
        <span class="truncate" appEllipsisTooltip [contrasted]="true">{{ item().label | transloco }}</span>
      </a>
    }
  `,
})
export class NavDevItemComponent {
  /** Navigation item data (a reserved top-level route). */
  readonly item = input.required<NavigationRootItem>();

  /** Current URL for active state detection. */
  readonly currentUrl = input.required<string>();

  /** Whether sidebar is collapsed. */
  readonly collapsed = input<boolean>(false);

  /**
   * Whether this item is currently active. The URL may carry a `/{tenant}`
   * prefix; strip it before comparing. For reserved top-level routes the strip
   * is a no-op (the first segment is not a tenant slug), so the comparison holds.
   */
  protected readonly isActive = computed(() =>
    isRouteActive(this.item().route, stripTenantSegment(this.currentUrl())),
  );

  /** Position and show the fixed tooltip. */
  protected showTooltip(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--tt-x', `${rect.right + 8}px`);
    el.style.setProperty('--tt-y', `${rect.top + rect.height / 2}px`);
    el.setAttribute('data-tooltip-visible', 'true');
  }

  /** Hide the tooltip. */
  protected hideTooltip(el: HTMLElement): void {
    el.setAttribute('data-tooltip-visible', 'false');
  }
}
